import * as React from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import styled from 'styled-components';

import { Category } from '@common/types/categories/category';
import { Dispatch } from 'redux';
import { UpdateField } from '@webapp/store/release/action-creators';
import { ReleaseRegion } from '@common/types/release/release-region';
import { formatRegions } from '@webapp/utils/normalizers';

import Radio from '@components/form/radio';
import DropdownCtrl from '@webapp/controllers/form/dropdown';
import RegionHeader from '@components/form/region/region-header';
import { Translate } from 'react-localize-redux';

interface TreeElement {
  name: string;
  children: ReleaseRegion[];
}

export interface RegionSelectorCtrlProps {
  title?: string;
  isMulti?: boolean;
  className?: string;
  children?: any;
  states?: any;
  regions: Category<ReleaseRegion>[];
  selectedRegions: ReleaseRegion[];
  updateRegions: (regions: ReleaseRegion[]) => UpdateField<'regions'>;
}

const mapStateToProps = (state: AppState) => ({
  regions: state.categories.lists.region || [],
  selectedRegions: state.release.release?.regions || [],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateRegions: (regions: ReleaseRegion[]) =>
    dispatch(new UpdateField<'regions'>('regions', regions)),
});

export const RegionSelectorCtrl: React.FC<RegionSelectorCtrlProps> = (
  props
) => {
  const [filter, setFilter] = React.useState('');
  const { isMulti = true, title } = props;

  const buildTree = (): TreeElement[] =>
    props.regions
      .reduce(
        (memo, c) =>
          memo.includes(c.metadata.parent)
            ? memo
            : memo.concat([c.metadata.parent]),
        [] as string[]
      )
      .map((c: string) => ({
        name: c,
        children: props.regions
          .filter((r) => {
            return (
              r.metadata.parent === c && (filter ? testFilter(r.name) : true)
            );
          })
          .map((r) => r.metadata)
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter((c) => (filter ? !!c.children.length : true))
      .sort((a, b) => a.name.localeCompare(b.name));

  const worldSelected = props.selectedRegions.length === props.regions.length;

  const isRegionActive = (name: string) => {
    return props.selectedRegions.some((t) => t.name === name);
  };

  const isContinentActive = (children: ReleaseRegion[]) => {
    return children.some((c) => isRegionActive(c.name));
  };

  const handleUpdateRegions = (regions: ReleaseRegion[]) => {
    setFilter('');

    return props.updateRegions(regions);
  };

  const handleWorldChange = () => {
    let regionsSelected: ReleaseRegion[] = props.selectedRegions || [];

    if (worldSelected) {
      regionsSelected = [];
    } else {
      regionsSelected = props.regions.map((r) => r.metadata);
    }

    props.updateRegions(regionsSelected);
  };

  const handleContinentChange = (
    children: ReleaseRegion[],
    active: boolean
  ) => {
    const regions = worldSelected
      ? props.regions.map((r) => r.metadata)
      : props.selectedRegions;
    const filteredRegions = regions.filter(
      (r) => !children.find((c) => r.id === c.id)
    );
    if (isMulti) {
      if (active) {
        return handleUpdateRegions(filteredRegions.concat(children));
      }

      return handleUpdateRegions(filteredRegions);
    } else {
      if (active) {
        return handleUpdateRegions(children);
      }

      return handleUpdateRegions(filteredRegions);
    }
  };

  const handleRegionChange = (region: ReleaseRegion, active: boolean) => {
    const regions = worldSelected
      ? props.regions.map((r) => r.metadata)
      : props.selectedRegions;
    const filteredRegions = regions.filter((r) => r.id !== region.id);

    if (isMulti) {
      if (active) {
        return handleUpdateRegions(filteredRegions.concat([region]));
      }

      return handleUpdateRegions(filteredRegions);
    } else {
      if (active) {
        return handleUpdateRegions([region]);
      }

      return handleUpdateRegions(filteredRegions);
    }
  };

  const testFilter = (name: string) => {
    return name.toLowerCase().includes(filter.toLowerCase());
  };

  const inFilter = (children: ReleaseRegion[]) => {
    return children.some((c) => testFilter(c.name));
  };

  const renderTree = (tree: TreeElement) => (
    <RegionHeader
      hasDropdown={!!tree.children.length}
      title={tree.name}
      key={`region-continent-${tree.name}`}
      isChecked={isContinentActive(tree.children)}
      onChange={(active) => handleContinentChange(tree.children, active)}
      opened={filter ? inFilter(tree.children) : false}
    >
      {tree.children.map((t) => (
        <Radio
          key={`region-region-${t.id}`}
          isChecked={isRegionActive(t.name)}
          className="child-region"
          onChange={(active) => handleRegionChange(t, active)}
        >
          {t.name}
        </Radio>
      ))}
    </RegionHeader>
  );

  const getValue = () => {
    if (worldSelected) {
      // return <Translate id="RELEASE.REGION_SELECTOR.WORLDWIDE" />;
      return 'Worldwide';
    }
    const filteredContinents = buildTree().filter((continent) =>
      continent.children.every((c) => {
        if (isRegionActive(c.name)) {
          props.selectedRegions.filter((i) => i.name !== c.name);
        }
        return isRegionActive(c.name);
      })
    );
    const copySelectedRegions = props.selectedRegions.filter(
      (country) =>
        !filteredContinents.some(
          (continent) => continent.name === country.parent
        )
    );

    return formatRegions(copySelectedRegions, filteredContinents);
  };

  const handleFilter = (filterValue: string) => {
    setFilter(filterValue.toLowerCase());
  };

  const getTitle = () => {
    if (props.selectedRegions.length === 0) {
      return <Translate id="RELEASE.COUNTRY_SELECTOR.TITLE_MISSING" />;
    } else {
      return title ? title : <Translate id="RELEASE.REGION_SELECTOR.TITLE" />;
    }
  };

  return (
    <DropdownCtrl
      className={props.className}
      title={getTitle()}
      value={getValue()}
      contentType="region"
      filterLabel={<Translate id="RELEASE.REGION_SELECTOR.FILTER_TITLE" />}
      customFilterValue={filter}
      onCustomFilter={handleFilter}
      hasFilter
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{ maxHeight: '250px' }}
      >
        <RegionHeader
          title={<Translate id="RELEASE.REGION_SELECTOR.WORLDWIDE" />}
          isChecked={worldSelected}
          onChange={() => handleWorldChange()}
        />
        {buildTree().map((t) => renderTree(t))}
      </div>
    </DropdownCtrl>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(RegionSelectorCtrl)`
  max-width: 100%;

  .child-region {
    margin: 14px 0 0 36px;
  }
`);
